import React from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  List,
  Row,
  Skeleton,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import type { GoodsProps, UsedInComboRow } from '../../../api/Goods';
import type { RemnantsManageRow } from '../../../api/RemnantsManage';
import type { MpLinkRow } from '../useGoodsDetail';
import type { ProductQuestionItem } from '../../../api/ProductQuestions';
import {
  isQuarantineActive,
  rowMpView,
} from '../fbs/fbsDisplayHelpers';

const { Text, Title } = Typography;

const val = (v?: string | null) =>
  v?.trim() ? v : <Text type="warning">Не заполнено</Text>;

type Props = {
  data: GoodsProps;
  mpRows: MpLinkRow[];
  usedInCombos: UsedInComboRow[];
  stockRows: RemnantsManageRow[];
  questionsNew: number;
  questions: ProductQuestionItem[];
  sideLoading?: boolean;
  questionsLoading?: boolean;
  onEdit: () => void;
  onEditCombo: () => void;
  onGoTab: (tab: string) => void;
  onBind: (authId: number) => void;
};

export const OverviewTab = ({
  data,
  mpRows,
  usedInCombos,
  stockRows,
  questionsNew,
  sideLoading,
  questionsLoading,
  onEdit,
  onEditCombo,
  onGoTab,
  onBind,
}: Props) => {
  const issues: { key: string; text: string; action?: () => void }[] = [];
  if (!data.name?.trim())
    issues.push({ key: 'name', text: 'Не заполнено название', action: onEdit });
  if (!data.brand?.name?.trim())
    issues.push({ key: 'brand', text: 'Не заполнен бренд', action: onEdit });
  if (!sideLoading) {
    const unlinked = mpRows.filter((r) => !r.product);
    if (unlinked.length)
      issues.push({
        key: 'mp',
        text: `Нет связей с МП (${unlinked.length})`,
        action: () => onGoTab('marketplaces'),
      });
    const archivedLinked = mpRows.filter((r) => r.product?.is_archived);
    if (archivedLinked.length)
      issues.push({
        key: 'archived',
        text:
          archivedLinked.length === 1
            ? 'Связанный товар удалён на МП'
            : `Связанных удалённых на МП: ${archivedLinked.length}`,
        action: () => onGoTab('marketplaces'),
      });
    if (!data.product_remnants?.show_for_distribution) {
      issues.push({
        key: 'fbs-off',
        text: 'FBS не включён',
        action: onEdit,
      });
    }
    for (const row of stockRows) {
      if (!row.mp_linked) {
        issues.push({
          key: 'fbs-no-link',
          text: 'Нет связи с МП для FBS',
          action: () => onGoTab('stocks'),
        });
        break;
      }
    }
    for (const row of stockRows) {
      const view = rowMpView(row, row.marketplace);
      if (isQuarantineActive(view.quarantine_at)) {
        issues.push({
          key: 'fbs-quarantine',
          text: 'Активен карантин FBS',
          action: () => onGoTab('stocks'),
        });
        break;
      }
    }
    for (const row of stockRows) {
      const view = rowMpView(row, row.marketplace);
      if (view.blocked) {
        issues.push({
          key: 'fbs-block',
          text: 'Отправка на МП заблокирована',
          action: () => onGoTab('stocks'),
        });
        break;
      }
    }
    for (const row of stockRows) {
      if (row.in_sale === false) {
        issues.push({
          key: 'fbs-off-sale',
          text: 'Товар снят с продажи (FBS)',
          action: () => onGoTab('stocks'),
        });
        break;
      }
    }
    for (const row of stockRows) {
      const view = rowMpView(row, row.marketplace);
      if (
        view.mp_stock != null &&
        view.target != null &&
        view.mp_stock !== view.target
      ) {
        issues.push({
          key: 'fbs-mp-target',
          text: 'Остаток на МП не совпадает с целевым',
          action: () => onGoTab('stocks'),
        });
        break;
      }
    }
  }
  if (!questionsLoading && questionsNew > 0) {
    issues.push({
      key: 'q',
      text: `${questionsNew} новый вопрос`,
      action: () => onGoTab('questions'),
    });
  }

  const composition = data.comboComposition || [];
  const hasComboSide =
    data.is_combo || composition.length > 0 || usedInCombos.length > 0;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Section
          title="Основная информация"
          action={
            <Button type="text" onClick={onEdit}>
              Редактировать
            </Button>
          }
        >
          <Descriptions column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="Артикул">{data.article}</Descriptions.Item>
            <Descriptions.Item label="Название">{val(data.name)}</Descriptions.Item>
            <Descriptions.Item label="Бренд">
              {val(data.brand?.name)}
            </Descriptions.Item>
            <Descriptions.Item label="Тип">
              {data.is_combo ? 'Комбо' : 'Обычный товар'}
            </Descriptions.Item>
            <Descriptions.Item label="FBS">
              {data.product_remnants?.show_for_distribution
                ? 'Включён'
                : 'Выключен'}
            </Descriptions.Item>
            <Descriptions.Item label="Категория">
              {data.category?.name || (
                <Text type="secondary">Не заполнено</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Теги" span={2}>
              {(data.tags || []).length ? (
                <Space size={[4, 4]} wrap>
                  {(data.tags || []).map((t) => (
                    <Tag key={t.id ?? t.name}>{t.name}</Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">Нет тегов</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Section>

        <Section
          title="Комбо"
          action={
            <Button type="text" onClick={onEditCombo}>
              Настроить
            </Button>
          }
        >
          {sideLoading && !data.is_combo && composition.length === 0 ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : !hasComboSide ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Товар не является комбо и не входит в состав других комбо"
            >
              <Button onClick={onEditCombo}>Создать комбо</Button>
            </Empty>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {data.is_combo && (
                <div>
                  <Text strong>Состав</Text>
                  {composition.length === 0 ? (
                    <div>
                      <Text type="secondary">Пустой состав</Text>
                    </div>
                  ) : (
                    <Table
                      size="small"
                      pagination={false}
                      style={{ marginTop: 8 }}
                      rowKey="article"
                      dataSource={composition}
                      columns={[
                        {
                          title: 'Артикул',
                          dataIndex: 'article',
                          render: (a: string) => (
                            <Link
                              to={`/settings/goods/${encodeURIComponent(a)}`}
                              target="_blank"
                            >
                              {a}
                            </Link>
                          ),
                        },
                        { title: 'Кол-во', dataIndex: 'value', width: 80 },
                      ]}
                    />
                  )}
                </div>
              )}
              {sideLoading && usedInCombos.length === 0 ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : usedInCombos.length > 0 ? (
                <div>
                  <Text strong>Входит в комбо</Text>
                  <Table
                    size="small"
                    pagination={false}
                    style={{ marginTop: 8 }}
                    rowKey="article"
                    dataSource={usedInCombos}
                    columns={[
                      {
                        title: 'Комбо',
                        dataIndex: 'article',
                        render: (a: string, r) => (
                          <Link
                            to={`/settings/goods/${encodeURIComponent(a)}`}
                            target="_blank"
                          >
                            {a}
                            {r.name ? ` · ${r.name}` : ''}
                          </Link>
                        ),
                      },
                      { title: 'Кол-во', dataIndex: 'value', width: 80 },
                    ]}
                  />
                </div>
              ) : null}
            </Space>
          )}
        </Section>

        <Section
          title="Связи с маркетплейсами"
          action={
            <Button type="link" onClick={() => onGoTab('marketplaces')}>
              Все связи →
            </Button>
          }
        >
          {sideLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : (
            <List
              size="small"
              dataSource={mpRows}
              locale={{ emptyText: 'Нет интеграций' }}
              renderItem={(row) => (
                <List.Item
                  actions={[
                    row.product ? (
                      <Button
                        key="go"
                        type="link"
                        size="small"
                        onClick={() => onGoTab('marketplaces')}
                      >
                        Открыть
                      </Button>
                    ) : (
                      <Button
                        key="bind"
                        size="small"
                        type="primary"
                        onClick={() => onBind(row.auth.id)}
                      >
                        Привязать
                      </Button>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Link
                        to={`/settings/auth_marketplace/${encodeURIComponent(
                          row.auth.marketplace,
                        )}`}
                      >
                        {row.auth.marketplace} #{row.auth.id}
                      </Link>
                    }
                    description={
                      row.product ? (
                        <Space wrap size={[8, 4]}>
                          <span>
                            {row.product.seller_sku}
                            {row.product.title
                              ? ` · ${row.product.title}`
                              : ''}
                          </span>
                          <Tag color="processing">Связана</Tag>
                          {row.product.is_archived ? (
                            <Tag>Удалённый на МП</Tag>
                          ) : (
                            <Tag color="success">Активный на МП</Tag>
                          )}
                        </Space>
                      ) : (
                        <Badge status="warning" text="Требуется привязка" />
                      )
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Section>
      </Col>

      <Col xs={24} lg={10}>
        <Section title="Требуют внимания">
          {sideLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : issues.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Критичных проблем нет"
            />
          ) : (
            <List
              size="small"
              dataSource={issues}
              renderItem={(item) => (
                <List.Item
                  actions={
                    item.action
                      ? [
                          <Button
                            key="go"
                            type="link"
                            size="small"
                            onClick={item.action}
                          >
                            Исправить
                          </Button>,
                        ]
                      : undefined
                  }
                >
                  <Badge status="warning" text={item.text} />
                </List.Item>
              )}
            />
          )}
        </Section>
      </Col>
    </Row>
  );
};

const Section = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card
    bordered={false}
    style={{
      marginBottom: 16,
      borderRadius: 8,
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    }}
    title={
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        {action}
      </div>
    }
  >
    {children}
  </Card>
);
